import moment from 'moment'
import type { RefObject } from 'react'
import { useCallback, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'

import Calendar from './Calendar'
import type {
  CalendarDay,
  CalendarDaySlot,
  CalendarSelection,
  CalendarSpan,
  CalendarSpanSegment,
} from './Calendar.types'
import CalendarDayCell from './CalendarDayCell'
import { addCalendarDays, toCalendarDate, toMonthKey } from './calendarDate'

import type { TripStop } from 'tripPlanner/api/types'

import { StopCalendarSpanBar } from './StopCalendarPresentation'
import { useAlignedMonth } from './calendarPanelLayout'
import { useTripCalendarMonthHeader } from './TripCalendarMonthHeader'
import TripCalendarShell from './TripCalendarShell'
import {
  buildBookedAccommodationSpans,
  buildStopSpans,
  type StopCalendarSpanData,
} from './stopCalendarOverlays'

interface Props {
  /** Every stop on the trip, drawn behind the date choice as context. */
  tripStops?: ReadonlyArray<TripStop>
  /**
   * The stop this search belongs to. Its bar keeps full presence while the
   * rest of the trip steps back, so it is clear which stop the dates are for.
   */
  contextStopId?: number
  startDate?: moment.Moment
  endDate?: moment.Moment
  /** Earliest selectable day, and the first day the calendar renders. */
  minDate: Date
  /** Last selectable day, and the last month the calendar can page to. */
  maxDate: Date
  /** Longest stay the search allows, counted in nights. */
  maxNights?: number
  /** Whether check-in and check-out may land on the same day, as an experience allows. */
  allowSameDayRange?: boolean
  /**
   * Month to open on before a check-in has been picked. A viewport hint
   * only — it never selects a date.
   */
  focusMonth?: Date
  onDatesChange: (dates: { startDate: moment.Moment, endDate?: moment.Moment }) => void
  /**
   * `scroll` stacks one month above the next and lets the panel scroll
   * through them. `paged` shows one month at a time with chevrons instead.
   *
   * @default 'scroll'
   */
  layout?: 'paged' | 'scroll'
  /** The scrollable ancestor driving `scroll` layout. */
  scrollContainerRef?: RefObject<HTMLElement | null>
  className?: string
}

/**
 * Choosing check-in and check-out against the rest of the trip.
 *
 * The same calendar and stop bars as the date editor, minus every way of
 * changing the itinerary: the stops here are read-only context, so it
 * is obvious that Melbourne already holds the 12th to the 15th before a hotel
 * in Sydney is booked over the top of it. Bars sit in the grid's own lane
 * layer, which does not take pointer events, so every click still belongs to
 * the day underneath.
 */
function TripContextSearchCalendar(props: Props) {
  const {
    tripStops,
    contextStopId,
    startDate,
    endDate,
    minDate,
    maxDate,
    maxNights,
    allowSameDayRange,
    focusMonth,
    onDatesChange,
    layout = 'scroll',
    scrollContainerRef,
    className,
  } = props
  const intl = useIntl()

  const from = useMemo(() => toCalendarDate(minDate), [minDate])
  const to = useMemo(() => toCalendarDate(maxDate), [maxDate])
  const checkIn = useMemo(() => startDate && toCalendarDate(startDate.toDate()), [startDate])
  const checkOut = useMemo(() => endDate && toCalendarDate(endDate.toDate()), [endDate])

  const spans = useMemo<ReadonlyArray<CalendarSpan<StopCalendarSpanData>>>(() => [
    ...buildStopSpans(tripStops),
    ...buildBookedAccommodationSpans(tripStops),
  ], [tripStops])

  const selection = useMemo<CalendarSelection>(() => ({
    mode: 'range',
    range: checkIn ? { from: checkIn, to: checkOut } : undefined,
  }), [checkIn, checkOut])

  /**
   * Days past the longest allowed stay grey out while a check-in is
   * half-chosen, the way the standard search calendar closes off an over-long
   * range rather than swallowing the click.
   */
  const lastSelectableDate = useMemo(() => {
    if (!checkIn || checkOut || maxNights === undefined) return undefined
    return addCalendarDays(checkIn, maxNights)
  }, [checkIn, checkOut, maxNights])

  const isDayDisabled = useCallback(
    (day: CalendarDay) => !!lastSelectableDate && day.date > lastSelectableDate,
    [lastSelectableDate],
  )

  /**
   * The month the grid should re-open on, as opposed to the one it happens to
   * be showing. Only an externally supplied hint counts: a check-in the
   * traveller just picked must never re-anchor the calendar, or choosing the
   * first half of a range would throw away every month they scrolled through
   * to find it.
   */
  const hintMonth = useMemo(
    () => focusMonth ? toMonthKey(toCalendarDate(focusMonth)) : undefined,
    [focusMonth],
  )

  const defaultMonth = useMemo(() => {
    const anchor = checkIn ?? (focusMonth && toCalendarDate(focusMonth)) ?? from
    return toMonthKey(anchor)
  }, [checkIn, focusMonth, from])

  /*
   * The month to bring to the top: whatever the panel opened on, until an
   * external hint says otherwise. Never the picked date — realigning on every
   * click would fight the traveller's own scrolling.
   *
   * The hint is kept once given. A caller stops hinting the moment the
   * traveller has a check-in of their own, and a hint going away must not
   * read as a different month to open on: that would remount the grid and
   * throw away every month they had scrolled through, in the middle of
   * picking a range.
   */
  const openingMonthRef = useRef(defaultMonth)
  const lastHintMonthRef = useRef(hintMonth)
  if (hintMonth) lastHintMonthRef.current = hintMonth
  const openingMonth = lastHintMonthRef.current ?? openingMonthRef.current
  useAlignedMonth(openingMonth, scrollContainerRef, layout === 'scroll')

  const onSelect = useCallback((next: CalendarSelection) => {
    if (next.mode !== 'range' || !next.range) return
    onDatesChange({
      startDate: moment(next.range.from),
      endDate: next.range.to ? moment(next.range.to) : undefined,
    })
  }, [onDatesChange])

  const renderDay = useCallback((slot: CalendarDaySlot<never, StopCalendarSpanData>) => {
    const isOverMaxStay = !!lastSelectableDate && slot.day.date > lastSelectableDate

    return <CalendarDayCell
      date={slot.day.date}
      day={slot.day.day}
      state={slot.state}
      isAdjacentMonth={slot.day.isAdjacentMonth}
      isToday={slot.day.isToday}
      isUnavailable={isOverMaxStay}
      laneCount={slot.laneCount}
      disabled={slot.day.isOutOfBounds || isOverMaxStay}
      onSelect={slot.select}
      data-testid={`day-${slot.day.date}`}
    />
  }, [lastSelectableDate])

  /** Where the grid stops, so a stop it cuts off is still named. */

  const renderSpan = useCallback((segment: CalendarSpanSegment<StopCalendarSpanData>) => {
    const spanData = segment.span.data
    const stopSpan = spanData && !('kind' in spanData) ? spanData : undefined
    // With no stop in context nothing is the odd one out, so nothing dims.
    const isContextStop = contextStopId === undefined || stopSpan?.stopId === contextStopId

    return <StopCalendarSpanBar
      segment={segment}
      emphasis={isContextStop ? 'normal' : 'muted'}
      // No drag handles here, so consecutive stops need no room between them.
      boundaryGap="tight"
    />
  }, [contextStopId])

  const renderMonthHeader = useTripCalendarMonthHeader(layout === 'scroll')

  return <TripCalendarShell className={className} $density="fit-month">
    <Calendar<never, StopCalendarSpanData>
    /*
     * `scroll` layout reads `defaultMonth` once, when it builds its window of
     * mounted months. A trip's month hint can arrive after that, so the grid
     * is remounted to re-open on it — keyed on the hint alone, never on the
     * selection.
     */
      key={layout === 'scroll' ? lastHintMonthRef.current : undefined}
      label={intl.formatMessage({
        id: 'e7VYTV6NxRU-hBzqDxu1',
        defaultMessage: 'Choose your check-in and check-out dates, with the rest of your trip shown for reference',
        description: 'Accessible name for the hotel search calendar inside the trip planner, which also shows the other stops on the trip',
      })}
      previousMonthLabel={intl.formatMessage({
        id: 'l2zZwkAg-S3MzeMEIMj6',
        defaultMessage: 'Previous month',
        description: 'Accessible label for the button moving the trip planner stop calendar back one month',
      })}
      nextMonthLabel={intl.formatMessage({
        id: 'IXfUr38BRguYu9dc6m6r',
        defaultMessage: 'Next month',
        description: 'Accessible label for the button moving the trip planner stop calendar forward one month',
      })}
      from={from}
      to={to}
      spans={spans}
      selectionMode="range"
      value={selection}
      onSelect={onSelect}
      maxRangeLength={maxNights}
      rangeUnit="night"
      allowSameDayRange={allowSameDayRange}
      isDayDisabled={isDayDisabled}
      renderDay={renderDay}
      renderSpan={renderSpan}
      renderMonthHeader={renderMonthHeader}
      defaultMonth={defaultMonth}
      focusMonth={openingMonth}
      layout={layout}
      scrollContainerRef={scrollContainerRef}
    />
  </TripCalendarShell>
}

export default TripContextSearchCalendar
